// ============================================================================
// @orgazone/core-analytics — cœur pur MÉTRIQUES & segmentation (SPEC §24.6quater). UTILITÉ : les
// DÉFINITIONS des indicateurs (CA, marge, panier, IDV, taux clients identifiés, contribution,
// RFM…) et l'agrégation dimensionnelle, appliquées de façon IDENTIQUE en caisse (petit jeu, sa
// journée) et au BO (masse) → réconciliation structurelle « jamais deux vérités ».
//
// DOCTRINE (§24.6quater) :
//  • AGNOSTIQUE AUX DIMENSIONS : reçoit des lignes enrichies + un jeu de dimensions GÉNÉRIQUE
//    (`dims`), groupe par n'importe quelle clé. Ne code JAMAIS « saison » ni « PMP » en dur →
//    sert retail ET tout métier sans saison/coût (il omet simplement ces clés).
//  • COÛT INJECTÉ : le cœur ne calcule jamais le coût — il reçoit `costHtCents` (fiche article).
//    MARGE = PV HT − coût HT, TVA TOUJOURS exclue. Ratios : taux de marque = marge/PV HT.
//  • Arithmétique ENTIÈRE via @orgazone/core-money (arrondi §30.3, ratios sans flottant).
// ============================================================================
import { divRoundHalfUp, mulDivRound, ratioScaled, pctBp } from '@orgazone/core-money';

// ── Ligne de vente ENRICHIE injectée (la « matière » : pièce fiscale + attributs article résolus) ──
export interface SaleLine {
  saleId: string; // ticket → compte les VENTES distinctes (panier, IDV, taux clients)
  seller?: string;
  clientId?: string; // présent = vente IDENTIFIÉE (taux de clients identifiés)
  qty: number; // quantité vendue (pièces ou unités mineures)
  netTtcCents: number; // net TTC de la ligne après remises (SIGNÉ : retour négatif)
  netHtCents: number; // net HT de la ligne
  costHtCents?: number; // coût HT TOTAL de la ligne (qté × prix d'achat), INJECTÉ ; absent → marge non calculable
  dims?: Record<string, string | undefined>; // dimensions PRODUCT-DEFINED (family, sub_family, brand, supplier, season, hour…)
}

// ── Mesures agrégées sur un ENSEMBLE de lignes ──
export interface Measures {
  caTtcCents: number;
  caHtCents: number;
  qty: number; // quantité NETTE (signée : retours négatifs) — pour le prix moyen / valorisation
  qtySold: number; // quantité VENDUE (lignes POSITIVES seules) — un retour ne « vend » pas −1 article (IDV)
  nbSales: number; // tickets distincts (toutes opérations présentes dans le lot)
  nbSaleTickets: number; // tickets ayant VENDU (≥ 1 ligne positive) — dénominateur IDV → jamais < 1 article/vente
  nbLines: number;
  costHtCents: number; // Σ coûts (0 si aucun)
  marginHtCents: number | null; // null si AU MOINS une ligne sans coût (marge non fiable)
}

// ── measures — agrège les mesures brutes d'un ensemble de lignes (une passe, déterministe) ──
export function measures(lines: SaleLine[]): Measures {
  let caTtc = 0;
  let caHt = 0;
  let qty = 0;
  let qtySold = 0;
  let cost = 0;
  let allCosted = true;
  const sales = new Set<string>();
  const saleTickets = new Set<string>();
  for (const l of lines) {
    caTtc += l.netTtcCents;
    caHt += l.netHtCents;
    qty += l.qty;
    sales.add(l.saleId);
    // IDV : ne compte que les lignes VENDUES (positives) et les tickets qui ont vendu → « articles / vente »
    // reste ≥ 1 (un retour, ligne négative, ne retranche pas d'articles vendus ni ne crée un « ticket de vente »).
    if (l.qty > 0) {
      qtySold += l.qty;
      saleTickets.add(l.saleId);
    }
    if (l.costHtCents === undefined) allCosted = false;
    else cost += l.costHtCents;
  }
  return {
    caTtcCents: caTtc,
    caHtCents: caHt,
    qty,
    qtySold,
    nbSales: sales.size,
    nbSaleTickets: saleTickets.size,
    nbLines: lines.length,
    costHtCents: cost,
    marginHtCents: lines.length > 0 && allCosted ? caHt - cost : null,
  };
}

// ── Dérivées (chacune : sa définition en une ligne) — entières, sans flottant, via core-money ──

/** Panier moyen (CA TTC / nb ventes), en centimes. 0 vente → 0. */
export function panierMoyenCents(m: Measures): number {
  return m.nbSales > 0 ? divRoundHalfUp(m.caTtcCents, m.nbSales) : 0;
}
/**
 * Une quantité en MILLIÈMES entiers — l'unité de la ligne scellée (`qty_milli`).
 * ⚠️ UNE QUANTITÉ N'EST PAS TOUJOURS ENTIÈRE : une vente au poids (0,250 kg), une reprise à quantité décimale, et la
 *    somme de flottants (0,1 + 0,2) qui n'en redonne pas un. Les ratios de `core-money` refusent un non-entier : passée
 *    telle quelle, la quantité ferait tomber tout l'écran qui l'affiche.
 */
const milli = (q: number): number => Math.round(q * 1000);

/** Indice de vente = articles VENDUS / ticket de VENTE, à `scale` décimales (défaut 1 → 4,2 renvoie 42).
 *  Base VENDUE (positive) → jamais < 1 article/vente, même en présence de retours (§17.2). */
export function idvScaled(m: Measures, scale = 1): number {
  return ratioScaled(milli(m.qtySold), m.nbSaleTickets * 1000, scale);
}
/** Prix moyen de vente (CA TTC / quantité), en centimes. 0 qté → 0. */
export function prixMoyenCents(m: Measures): number {
  const q = milli(m.qty);
  return q > 0 ? mulDivRound(m.caTtcCents, 1000, q) : 0;
}
/** Taux de marque = marge HT / CA HT, en points de base. null si marge non calculable OU CA HT ≤ 0. */
export function tauxMarqueBp(m: Measures): number | null {
  // CA HT ≤ 0 (retours ≥ ventes) → taux NON défini (null), pas « 0 % » : distingue « marge nulle »
  // d'« indéfini » (sinon une journée à perte de marge s'afficherait « 0,00 % »).
  if (m.marginHtCents === null || m.caHtCents <= 0) return null;
  return pctBp(m.marginHtCents, m.caHtCents);
}
/** Contribution d'un sous-total au total (part), en points de base. */
export function contributionBp(partCaCents: number, totalCaCents: number): number {
  return pctBp(partCaCents, totalCaCents);
}
/** Taux de clients identifiés = tickets AVEC client / tickets, en points de base. */
export function tauxClientsIdentifiesBp(lines: SaleLine[]): number {
  const { identifiees, total } = ventesIdentifiees(lines);
  return total > 0 ? pctBp(identifiees, total) : 0;
}

/**
 * Le DÉCOMPTE derrière le taux : combien de ventes portent un client, sur combien.
 *
 * ⚠️ EXTRAIT DU TAUX, PAS RECALCULÉ À CÔTÉ. L'écran affiche « 412 tickets sur 1 084 » sous le
 * pourcentage : reconstituer 412 en multipliant le taux par le nombre de ventes donnerait un nombre
 * FAUX de quelques unités (l'arrondi du taux), et deux chiffres qui ne s'accordent pas dans la même
 * carte détruisent la confiance dans les deux.
 */
export function ventesIdentifiees(lines: SaleLine[]): { identifiees: number; total: number } {
  const withClient = new Set<string>();
  const all = new Set<string>();
  for (const l of lines) {
    all.add(l.saleId);
    if (l.clientId) withClient.add(l.saleId);
  }
  return { identifiees: withClient.size, total: all.size };
}

// ── Agrégation DIMENSIONNELLE (le cœur du « stats par fournisseur/famille/saison… ») ──

/** Regroupe les lignes par la valeur d'une dimension et calcule les mesures de chaque groupe.
 *  Les lignes dont la dimension est absente tombent dans le bucket `fallback` (défaut « (non renseigné) »). */
export function groupByDim(lines: SaleLine[], dimKey: string, fallback = '(non renseigné)'): Record<string, Measures> {
  const buckets = new Map<string, SaleLine[]>();
  for (const l of lines) {
    const key = l.dims?.[dimKey] ?? fallback;
    const arr = buckets.get(key);
    if (arr) arr.push(l);
    else buckets.set(key, [l]);
  }
  const out: Record<string, Measures> = {};
  for (const [key, arr] of buckets) out[key] = measures(arr);
  return out;
}

/** Classement des valeurs d'une dimension par une mesure (top vendeurs, top familles, top fournisseurs). */
export function topByMeasure(
  lines: SaleLine[],
  dimKey: string,
  pick: (m: Measures) => number,
  limit = 10,
): Array<{ key: string; value: number; measures: Measures }> {
  const grouped = groupByDim(lines, dimKey);
  return Object.entries(grouped)
    .map(([key, m]) => ({ key, value: pick(m), measures: m }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

// ── Comparatif temporel (vs N-1 / période précédente) ──

/** Croissance (courant − précédent) / précédent, en points de base. Précédent = 0 → 0 (indéfini). */
export function growthBp(current: number, previous: number): number {
  // Produit Δ × 10000 en BigInt via mulDivRound → aucun débordement sur gros volumes (doctrine §24.6bis A).
  return previous === 0 ? 0 : mulDivRound(current - previous, 10000, Math.abs(previous));
}

// ── RFM (Récence / Fréquence / Montant) — scoring par quintile + segment ──

/** Faits client injectés (calculés par le BO/caisse depuis l'historique des ventes du client). */
export interface RfmInput {
  recencyDays: number; // jours depuis le dernier achat (PLUS BAS = mieux)
  frequency: number; // nb de visites sur la fenêtre (PLUS HAUT = mieux)
  monetaryCents: number; // CA cumulé sur la fenêtre (PLUS HAUT = mieux)
}
/** Bornes de quintile (4 points de coupe croissants → 5 paliers) par axe — calculées sur la population au BO. */
export interface RfmBoundaries {
  recency: [number, number, number, number];
  frequency: [number, number, number, number];
  monetary: [number, number, number, number];
}
export interface RfmScore {
  r: number; // 1..5
  f: number;
  m: number;
  segment: string;
}

// Palier 1..5 selon 4 bornes croissantes ; `invert` pour la récence (moins de jours = meilleur score).
function quintile(value: number, cuts: [number, number, number, number], invert: boolean): number {
  let s = 1;
  for (const c of cuts) if (value >= c) s += 1;
  return invert ? 6 - s : s;
}

// ── rfmScore — score R/F/M (1..5) + segment lisible, à partir des faits client et des bornes population ──
export function rfmScore(input: RfmInput, b: RfmBoundaries): RfmScore {
  const r = quintile(input.recencyDays, b.recency, true);
  const f = quintile(input.frequency, b.frequency, false);
  const m = quintile(input.monetaryCents, b.monetary, false);
  return { r, f, m, segment: rfmSegment(r, f, m) };
}

// Segment marketing standard depuis les scores R/F/M (mapping par défaut — surchargeable au BO plus tard).
// ⚠️ Ordre CRITIQUE : « à risque » (lapsé mais ÉTAIT fréquent) doit être testé AVANT « fidèles »,
// sinon tout f≥4 partait en « fidèles » et les ex-champions en décrochage (cible n°1 de la reconquête)
// étaient masqués (§24.6quater B, déclencheur « inactif > X »).
function rfmSegment(r: number, f: number, m: number): string {
  if (r >= 4 && f >= 4 && m >= 4) return 'champions';
  if (r <= 2 && f >= 3) return 'à risque'; // lapsé (récence basse) mais fréquence encore haute
  if (f >= 4) return 'fidèles'; // fréquent ET pas lapsé (les lapsés sont partis en « à risque » ci-dessus)
  if (r >= 4 && f <= 2) return 'nouveaux';
  if (r <= 2 && f <= 2) return 'perdus';
  return 'réguliers';
}

// ── Évaluation de PRÉDICAT de segment — condition sur des métriques client (numériques OU catégorielles) ──
export type MetricValue = number | string;
export type PredicateOp = '>' | '>=' | '<' | '<=' | '==' | '!=' | 'in' | '!in';
export interface Condition {
  metric: string;
  op: PredicateOp;
  value: MetricValue | MetricValue[]; // `in`/`!in` → ENSEMBLE ; comparateurs numériques → nombre
}
// ── evalPredicate — vrai si TOUTES les conditions (ET) sont vérifiées sur les métriques fournies ──
// UTILITÉ : base de la segmentation/déclencheurs (§24.6quater B) — le cœur évalue un client, le BO
// exécute en masse. Gère les métriques NUMÉRIQUES (comparaisons) ET CATÉGORIELLES (segment RFM, ville…)
// via `in`/`!in` (appartenance à un ensemble : « RFM ∈ {champions,fidèles} », « CP ∈ [69001,69002,…] »).
// Métrique absente → faux (pas de crash) ; comparateur numérique sur une valeur non-nombre → faux (jamais NaN).
export function evalPredicate(metrics: Record<string, MetricValue>, conditions: Condition[]): boolean {
  return conditions.every((c) => {
    const v = metrics[c.metric];
    if (v === undefined) return false;
    switch (c.op) {
      case 'in':
        return Array.isArray(c.value) && c.value.includes(v);
      case '!in':
        return Array.isArray(c.value) && !c.value.includes(v);
      case '==':
        return v === c.value;
      case '!=':
        return v !== c.value;
      case '>':
      case '>=':
      case '<':
      case '<=': {
        if (typeof v !== 'number' || typeof c.value !== 'number') return false; // comparaison = numérique uniquement
        return c.op === '>' ? v > c.value : c.op === '>=' ? v >= c.value : c.op === '<' ? v < c.value : v <= c.value;
      }
    }
  });
}

// ── PALIERS DE LECTURE DES INDICATEURS ────────────────────────────────────────────────────────
//
// ⚠️ POURQUOI CES NOMBRES SONT ICI ET NON DANS L'ÉCRAN. Ce sont des règles MÉTIER, pas des choix de
// design : « sous 5 % de transformation, c'est catastrophique » est une affirmation sur le commerce
// de détail, pas sur une couleur. Écrits dans un écran, ils seraient recopiés au deuxième écran qui
// affiche le même indicateur — et les deux finiraient par ne plus dire la même chose.
//
// ⚠️ ET POURQUOI ILS EXISTENT. Sans paliers, une jauge n'a pas le droit de juger : elle reste à la
// couleur de marque (charte `gaugeTone`). Les poser, c'est décider — ces valeurs viennent d'un
// arbitrage utilisateur du 2026-09-04, elles ne sont pas déduites d'une moyenne du marché.
//
// ⚠️ EN POINTS DE BASE (10 000 = 100 %), l'unité des ratios de la plateforme : `pctBp` rend déjà
// cette unité, et convertir en pour-cent pour comparer ferait perdre les décimales du taux.

/** Deux bornes, trois bandes : sous la première c'est mauvais, au-dessus de la seconde c'est bon. */
export interface MetricBands {
  warnFromBp: number;
  goodFromBp: number;
  /**
   * LA QUATRIÈME BANDE — facultative, et elle DÉPLACE le vert au lieu de s'ajouter après lui.
   *
   * ⚠️ SANS ELLE : mauvais → moyen → bon (vert au-dessus de `goodFromBp`). AVEC elle : mauvais →
   * moyen → **jaune** → bon (vert au-dessus d'ici). Le vert reste TOUJOURS le haut de l'échelle, et
   * le jaune s'INSÈRE — c'est ce qui garde la rampe rouge/orange/jaune/vert lisible sans légende.
   *
   * ⚠️ DEUX PROPOSITIONS ONT ÉTÉ ESSAYÉES ET REJETÉES AVANT (2026-09-05, et les deux venaient de
   * moi) : une couleur AU-DESSUS du vert — aucune teinte ne se lit « mieux que vert », et la bande
   * du milieu serait passée pour un demi-échec ; puis un MOT sous les chiffres (« correct » /
   * « exceptionnel ») — « aucun texte, c'est pas bon », une jauge se lit d'un coup d'œil et lire
   * n'est pas regarder. Insérer une teinte dans la rampe résout ce que les deux ratent.
   */
  excellentFromBp?: number;
}


/**
 * TAUX DE TRANSFORMATION — visiteurs qui achètent.
 *
 * ⚠️ LES BORNES SONT BASSES, ET C'EST NORMAL. Un taux de transformation de 16 % est BON en commerce
 * physique : le dénominateur compte tous les passages, y compris ceux qui n'avaient aucune intention
 * d'acheter. Un lecteur habitué au commerce en ligne y verrait un échec ; l'appliquer avec les seuils
 * du web peindrait en rouge un magasin qui va très bien.
 * ⚠️ IL DEMANDE UN COMPTEUR DE PASSAGES. Sans lui, le taux n'est pas mesuré — et la jauge le DIT
 * (ton `unmeasured`) au lieu d'afficher zéro.
 */
// ⚠️ TROIS BORNES POUR CET INDICATEUR-LÀ (arbitrage utilisateur 2026-09-05) : « en dessous de 5
// rouge, entre 5 et 10 orange, au-delà de 10 vert ; au-delà de 10 c'est correct, au-delà de 15
// c'est exceptionnel ». La troisième borne ne peint rien — elle nomme (voir `excellentFromBp`).
export const CONVERSION_RATE_BANDS: MetricBands = {
  warnFromBp: 500, goodFromBp: 1_000, excellentFromBp: 1_500,
};

/**
 * PART DES VENTES RATTACHÉES À UNE FICHE CLIENT.
 *
 * ⚠️ CE N'EST PAS UN TAUX DE SATISFACTION, c'est une mesure de la qualité du fichier : sous 15 %, le
 * fichier client ne représente plus l'activité et tout ce qui en dérive (segments, campagnes,
 * fidélité) porte sur une minorité non représentative.
 */
export const IDENTIFIED_SALES_BANDS: MetricBands = { warnFromBp: 1_500, goodFromBp: 3_000 };

/**
 * LE MÊME JOUR DE SEMAINE, 52 SEMAINES PLUS TÔT (`AAAAMMJJ`) — le vis-à-vis de la comparaison N-1 : un samedi se compare à
 * un samedi, jamais à la même date tombée un vendredi, qui n'a aucun sens commercial.
 * ⚠️ 364 JOURS, PAS UN AN : une période garde sa longueur ET sa composition en jours de semaine (octobre 2026 a cinq
 *    samedis, sa fenêtre N-1 aussi, quand octobre 2025 n'en a que quatre). Elle commence un ou deux jours après la même
 *    date l'an dernier — l'écran affiche donc les dates réellement comparées. Les fêtes à date fixe ne sont pas recalées :
 *    leur écart reste visible plutôt que maquillé.
 * ⚠️ ÉCRITE UNE FOIS POUR L'ÉCRAN ET LE SERVEUR : l'écran qui propose « N-1, même jour de semaine » et le serveur qui la
 *    prend sans choix doivent demander les mêmes jours, sinon un même réglage rend deux comparaisons.
 */
export function sameWeekdayLastYear(ymd: string): string {
  return shiftDays(ymd, -364);
}

/**
 * LE JOUR DONT `ymd` EST LE VIS-À-VIS N-1 — 364 jours plus tard, l'inverse exact de `sameWeekdayLastYear`.
 * ⚠️ C'EST LUI QUI RANGE UNE VENTE N-1 DANS LE BON SEAU : replacée au jour qu'elle compare, elle tombe dans le jour, la
 *    semaine ou le mois de la période — un appariement par rang se décalerait dès qu'un jour fermé n'est pas le même des
 *    deux côtés.
 */
export function sameWeekdayNextYear(ymd: string): string {
  return shiftDays(ymd, 364);
}

/** Un jour `AAAAMMJJ` décalé de `n` jours, sur le calendrier UTC (aucun fuseau, aucune heure d'été). */
function shiftDays(ymd: string, n: number): string {
  const d = new Date(Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)) + n));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
}

// Les objectifs de vente : répartition du mois sur ses jours, profil des jours, règles de saisie.
export * from './objectives';
export * from './stock';
